import clsx from 'clsx';
import { Individual } from '@semantical/modules/knowledge-graph';
import type { DataProperty } from '@semantical/modules/ontology-model';
import { getInputType, getInputStep, formatDataPropertyCardinality } from './utils';

type Props = {
    prop: DataProperty;
    individual: Individual;
    index: number;
    focusedInput: string | null;
    onFocusChange: (key: string | null) => void;
    onValueChange: (individual: Individual, propertyUri: string, value: string | null) => void;
    onRefresh: () => void;
};

export default function EditableDataProperty({
    prop,
    individual,
    index,
    focusedInput,
    onFocusChange,
    onValueChange,
    onRefresh
}: Props) {
    const inputType = getInputType(prop.range);
    const inputStep = getInputStep(prop.range);
    const isCheckbox = inputType === 'checkbox';

    const maxCardinality = prop.cardinality?.exact ?? prop.cardinality?.max;
    const isMultiValue = maxCardinality === undefined || maxCardinality > 1;

    const currentValues = individual.getPropertyValues(prop.uri);
    const canAddMore = maxCardinality === undefined || currentValues.length < maxCardinality;

    if (!isMultiValue) {
        const currentValue = currentValues[0] || '';
        const hasValue = currentValues.length > 0;
        const isEditing = focusedInput === prop.uri;

        return (
            <div key={`editable-${index}`} className="flex items-start gap-1.5 mb-1.5 py-0.5 flex-wrap">
                <span className="text-[11px] text-slate-600 dark:text-white/75 min-w-20 flex-shrink-0 pt-1">{prop.label}:</span>
                <div className="flex flex-col gap-1 flex-1 min-w-0 items-stretch">
                    {isCheckbox ? (
                        <input
                            type="checkbox"
                            checked={currentValue === 'true'}
                            onChange={(e) => onValueChange(
                                individual,
                                prop.uri,
                                e.target.checked ? 'true' : 'false'
                            )}
                            className="mr-auto"
                        />
                    ) : hasValue && !isEditing ? (
                        <div
                            className="inline-flex items-center gap-1 px-2 py-[3px] bg-[rgba(100,180,255,0.15)] border border-[rgba(100,180,255,0.3)] rounded-xl text-[11px] text-[rgba(100,180,255,0.95)] cursor-pointer transition-all duration-150 flex-1 min-w-0 hover:bg-[rgba(100,180,255,0.25)] hover:border-[rgba(100,180,255,0.5)]"
                            title="Click to edit"
                            onClick={() => onFocusChange(prop.uri)}
                        >
                            <span className="overflow-hidden text-ellipsis whitespace-nowrap flex-1">{currentValue}</span>
                            <button
                                className="ml-1 px-0.5 text-[10px] bg-transparent border-none text-[rgba(255,100,100,0.7)] cursor-pointer leading-none"
                                tabIndex={-1}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onValueChange(individual, prop.uri, null);
                                }}
                                title="Clear value"
                            >
                                ×
                            </button>
                        </div>
                    ) : (
                        <input
                            type={inputType}
                            defaultValue={currentValue}
                            placeholder="Enter value..."
                            step={inputStep}
                            className={clsx(
                                "flex-1 px-1.5 py-[3px] text-[11px] bg-slate-100 dark:bg-white/10 border border-slate-200 dark:border-white/20 rounded text-slate-700 dark:text-white outline-none min-w-20",
                                isEditing && "border-accent-teal dark:border-accent/50 bg-slate-50 dark:bg-white/[0.15]"
                            )}
                            autoFocus={isEditing && hasValue}
                            onFocus={() => onFocusChange(prop.uri)}
                            onBlur={(e) => {
                                onFocusChange(null);
                                onValueChange(individual, prop.uri, e.target.value || null);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    (e.target as HTMLInputElement).blur();
                                } else if (e.key === 'Escape') {
                                    onFocusChange(null);
                                }
                            }}
                        />
                    )}
                </div>
                <span className="text-[9px] text-slate-400 dark:text-white/40 flex-shrink-0">
                    {formatDataPropertyCardinality(prop)}
                </span>
            </div>
        );
    }

    // Multi-value mode
    const addInputFocused = focusedInput === `${prop.uri}#add`;

    return (
        <div key={`editable-${index}`} className="flex items-start gap-1.5 mb-1.5 py-0.5 flex-wrap">
            <span className="text-[11px] text-slate-600 dark:text-white/75 min-w-20 flex-shrink-0 pt-1">{prop.label}:</span>
            <div className="flex flex-col gap-1 flex-1 min-w-0 items-stretch">
                {currentValues.map((value, valueIndex) => {
                    const editKey = `${prop.uri}#edit#${valueIndex}`;
                    const isEditing = focusedInput === editKey;

                    if (isEditing) {
                        return (
                            <input
                                key={editKey}
                                type={inputType}
                                defaultValue={value}
                                step={inputStep}
                                className="flex-1 px-1.5 py-[3px] text-[11px] bg-slate-100 dark:bg-white/10 border border-accent-teal dark:border-accent/50 bg-slate-50 dark:bg-white/[0.15] rounded text-slate-700 dark:text-white outline-none min-w-20"
                                autoFocus
                                onBlur={(e) => {
                                    onFocusChange(null);
                                    const newValue = e.target.value;
                                    if (newValue && newValue !== value) {
                                        individual.updatePropertyValue(prop.uri, value, newValue);
                                        onValueChange(individual, prop.uri, newValue);
                                    } else if (!newValue) {
                                        individual.removePropertyValue(prop.uri, value);
                                        const newValues = individual.getPropertyValues(prop.uri);
                                        onValueChange(
                                            individual,
                                            prop.uri,
                                            newValues.length > 0 ? newValues[0] : null
                                        );
                                    }
                                    onRefresh();
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        (e.target as HTMLInputElement).blur();
                                    } else if (e.key === 'Escape') {
                                        onFocusChange(null);
                                    }
                                }}
                            />
                        );
                    }

                    return (
                        <div
                            key={`${prop.uri}-${valueIndex}`}
                            className="inline-flex items-center gap-1 px-2 py-[3px] bg-[rgba(100,180,255,0.15)] border border-[rgba(100,180,255,0.3)] rounded-xl text-[11px] text-[rgba(100,180,255,0.95)] cursor-pointer transition-all duration-150 flex-1 min-w-0 hover:bg-[rgba(100,180,255,0.25)] hover:border-[rgba(100,180,255,0.5)]"
                            title="Click to edit"
                            onClick={() => onFocusChange(editKey)}
                        >
                            <span className="overflow-hidden text-ellipsis whitespace-nowrap flex-1">{value}</span>
                            <button
                                className="ml-1 px-0.5 text-[10px] bg-transparent border-none text-[rgba(255,100,100,0.7)] cursor-pointer leading-none"
                                tabIndex={-1}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    individual.removePropertyValue(prop.uri, value);
                                    const newValues = individual.getPropertyValues(prop.uri);
                                    onValueChange(
                                        individual,
                                        prop.uri,
                                        newValues.length > 0 ? newValues[0] : null
                                    );
                                    onRefresh();
                                }}
                                title="Remove"
                            >
                                ×
                            </button>
                        </div>
                    );
                })}

                {canAddMore && (
                    <input
                        type={inputType}
                        placeholder="Add value..."
                        step={inputStep}
                        className={clsx(
                            "flex-1 px-1.5 py-[3px] text-[11px] bg-slate-100 dark:bg-white/10 border border-slate-200 dark:border-white/20 rounded text-slate-700 dark:text-white outline-none min-w-20",
                            addInputFocused && "border-accent-teal dark:border-accent/50 bg-slate-50 dark:bg-white/[0.15]"
                        )}
                        onFocus={() => onFocusChange(`${prop.uri}#add`)}
                        onBlur={(e) => {
                            onFocusChange(null);
                            if (e.target.value) {
                                individual.addPropertyValue(prop.uri, e.target.value);
                                onValueChange(individual, prop.uri, e.target.value);
                                e.target.value = '';
                                onRefresh();
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                const input = e.target as HTMLInputElement;
                                if (input.value) {
                                    individual.addPropertyValue(prop.uri, input.value);
                                    onValueChange(individual, prop.uri, input.value);
                                    input.value = '';
                                    onRefresh();
                                }
                            }
                        }}
                    />
                )}
            </div>
            <span className="text-[9px] text-slate-400 dark:text-white/40 flex-shrink-0">
                {formatDataPropertyCardinality(prop)}
            </span>
        </div>
    );
}
